/**
 * WFS Query Cache and Optimization Utilities
 * 
 * This module provides caching and optimization for WFS queries to improve
 * frontend performance by reducing redundant API calls.
 */

class WFSCache {
  constructor(maxEntries = 100, ttl = 300000) { // 5 minutes TTL
    this.cache = new Map();
    this.maxEntries = maxEntries;
    this.ttl = ttl;
  }

  /**
   * Generate a cache key based on bbox and filters
   */
  generateKey(bbox, filters, layerType) {
    const sortedFilters = JSON.stringify(filters, Object.keys(filters).sort());
    return `${layerType}:${bbox}:${sortedFilters}`;
  }

  /**
   * Get cached result if still valid
   */
  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set cache entry
   */
  set(key, data) {
    // Remove oldest entries if cache is full
    if (this.cache.size >= this.maxEntries) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Clear all cache entries
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Clear entries for specific layer type
   */
  clearLayer(layerType) {
    for (const [key] of this.cache) {
      if (key.startsWith(`${layerType}:`)) {
        this.cache.delete(key);
      }
    }
  }
}

// Create singleton cache instance
export const wfsCache = new WFSCache();

/**
 * Debounce function to prevent excessive API calls
 */
/**
 * Sentinel for a debounced call that was replaced before it ran. Distinct from
 * a real result so a caller can tell "superseded" from "returned nothing".
 */
export const SUPERSEDED = Symbol('debounce:superseded');

/**
 * Debounce that returns a promise for the eventual call.
 *
 * The promise matters. This used to return undefined, so a caller doing
 * `Promise.allSettled([...])` saw an immediately-fulfilled value and never
 * learned whether the underlying fetch failed. The moveend handler in
 * gl-map-optimized relies on exactly that signal to undo its coverage record -
 * without it, a failed tenement fetch left the map believing that ground was
 * loaded, and the layer stayed empty there until the user panned clear of it.
 *
 * A superseded call is RESOLVED with SUPERSEDED rather than left pending.
 * Leaving it pending would hang any allSettled waiting on it forever, which
 * would break the same error handling this exists to enable.
 */
export function debounce(func, wait) {
  let timeout;
  let settlePending = null;
  return function executedFunction(...args) {
    clearTimeout(timeout);
    if (settlePending) {
      settlePending(SUPERSEDED);
      settlePending = null;
    }
    return new Promise((resolve, reject) => {
      settlePending = resolve;
      timeout = setTimeout(() => {
        settlePending = null;
        try {
          Promise.resolve(func(...args)).then(resolve, reject);
        } catch (err) {
          reject(err);
        }
      }, wait);
    });
  };
}

/**
 * NOTE: not currently called. Superseded by expandBbox + bboxContains above,
 * which answer the question the query path actually asks - "is the new
 * viewport already inside what we fetched?" - rather than "do these two
 * overlap by some fraction?".
 *
 * Check if two bounding boxes have significant overlap
 * to avoid redundant queries for similar areas
 */
export function bboxOverlap(bbox1, bbox2, threshold = 0.8) {
  const [w1, s1, e1, n1] = bbox1.split(',').map(Number);
  const [w2, s2, e2, n2] = bbox2.split(',').map(Number);

  const overlapW = Math.max(w1, w2);
  const overlapS = Math.max(s1, s2);
  const overlapE = Math.min(e1, e2);
  const overlapN = Math.min(n1, n2);

  if (overlapW >= overlapE || overlapS >= overlapN) return false;

  const overlapArea = (overlapE - overlapW) * (overlapN - overlapS);
  const bbox1Area = (e1 - w1) * (n1 - s1);

  return overlapArea / bbox1Area > threshold;
}

/**
 * Abort controller manager for canceling in-flight requests
 */
/**
 * Grows a bbox about its centre.
 *
 * Every WFS query used to ask for exactly the viewport, so the instant you
 * panned you were looking at ground with no data on it until the next request
 * came back. Fetching a margin means the next few pans are already covered.
 *
 * Measured on tenements around Kalgoorlie, factor 2 (double the width and
 * height, so 4x the area) costs only +83% payload rather than the +300% the
 * area suggests, because feature density falls off outside the cluster you are
 * looking at. That is the trade this is built on: a bit more per request, far
 * fewer requests.
 *
 * @param {string|number[]} bbox  "minx,miny,maxx,maxy" or [minx,miny,maxx,maxy]
 * @param {number} factor         2 = double width and height
 * @returns {number[]}            [minx, miny, maxx, maxy]
 */
export function expandBbox(bbox, factor = 2) {
  const [minx, miny, maxx, maxy] = (typeof bbox === 'string' ? bbox.split(',') : bbox).map(Number);
  const cx = (minx + maxx) / 2;
  const cy = (miny + maxy) / 2;
  const hw = ((maxx - minx) / 2) * factor;
  const hh = ((maxy - miny) / 2) * factor;
  return [
    cx - hw,
    // Clamp to the projection's valid range. Web Mercator dies at the poles,
    // and a WA-only product will never legitimately need beyond this.
    Math.max(cy - hh, -85),
    cx + hw,
    Math.min(cy + hh, 85),
  ];
}

/** True when `inner` lies entirely inside `outer`. */
export function bboxContains(outer, inner) {
  if (!outer || !inner) return false;
  const [oW, oS, oE, oN] = (typeof outer === 'string' ? outer.split(',') : outer).map(Number);
  const [iW, iS, iE, iN] = (typeof inner === 'string' ? inner.split(',') : inner).map(Number);
  return oW <= iW && oS <= iS && oE >= iE && oN >= iN;
}

export class RequestManager {
  constructor() {
    this.controllers = new Map();
  }

  /**
   * Create new abort controller for a request type
   */
  createController(requestType) {
    // Cancel existing request of same type
    this.cancelRequest(requestType);
    
    const controller = new AbortController();
    this.controllers.set(requestType, controller);
    return controller;
  }

  /**
   * Cancel specific request type
   */
  cancelRequest(requestType) {
    const controller = this.controllers.get(requestType);
    if (controller) {
      controller.abort();
      this.controllers.delete(requestType);
    }
  }

  /**
   * Cancel all requests
   */
  cancelAllRequests() {
    for (const controller of this.controllers.values()) {
      controller.abort();
    }
    this.controllers.clear();
  }
}

export const requestManager = new RequestManager();